import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  ComplaintCategory,
  CreateComplaintConfiguration,
  CreateComplaintRequest,
  CreateServiceConfiguartion,
  CreateServiceRequest,
  ServiceCategory,
  updateComplaintCategory,
  UpdateComplaintConfiguration,
  updateServiceCategory,
  updateServiceConfig,
  updateStatusRequest,
} from "./types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const getServiceCategory = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "service_type");
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const searchServiceCategory = async (
  remote_utility_id: string,
  search_data: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "search/onboarding_config",
    (query) => {
      query.push("search_field", "support_requests_json-service_type");
      query.push("search_data", search_data);
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "service_type");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getServiceSubCategory = async (
  remote_utility_id: string,
  code?: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "service_sub_type");
      if (code) {
        query.push("code_list", code);
      }
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getUtilityServices = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", remote_utility_id);
    }
  );

  const response = await authApiClient.get(url);
  return response.data.result ?? [];
};

export const createServiceCategory = async (
  data: CreateServiceRequest
): Promise<ServiceCategory> => {
  const payload = {
    utility_service: data.utility_service,
    name: data.name,
    code: data.code,
    remote_utility_id: data.remote_utility_id,
    request_type: data.request_type,
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createServiceSubCategory = async (
  data: CreateServiceRequest
): Promise<ServiceCategory> => {
  const payload = {
    utility_service: data.utility_service,
    name: data.name,
    code: data.code,
    remote_utility_id: data.remote_utility_id,
    request_type: data.request_type,
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateRequestStatus = async (
  remote_utility_id: any,
  data: updateStatusRequest
): Promise<ServiceCategory> => {
  const payload = {
    code: data.code,
    is_active: data.is_active,
    remote_utility_id: remote_utility_id,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateServiceSubCategory = async (
  data: updateServiceCategory
): Promise<ServiceCategory> => {
  const payload = {
    name: data.name,
    remote_utility_id: data.remote_utility_id,
    code: data.code,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCategory = async (
  remote_utility_id: string,
  category: any
): Promise<ServiceCategory> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "delete-onboarding-categories",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("request_type", "support_request_type");
        query.push("code", category.code);
      }
    );

    const response = await cxApiClient.delete(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteSubCategory = async (
  remote_utility_id: string,
  subCategory: any
): Promise<ServiceCategory> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "delete-onboarding-categories",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("request_type", "support_request_subtype");
        query.push("code", subCategory.code);
      }
    );

    const response = await cxApiClient.delete(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllComplaintCategory = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "complaint_type");
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const searchAllComplaintCategory = async (
  remote_utility_id: string,
  search_data: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "search/onboarding_config",
    (query) => {
      query.push("search_field", "support_requests_json-complaint_type");
      query.push("search_data", search_data);
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "complaint_type");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const createComplaintCategory = async (
  remote_utility_id: string,
  data: CreateComplaintRequest
): Promise<ServiceCategory> => {
  const payload = {
    utility_service: data.utility_service,
    name: data.name,
    code: data.code,
    remote_utility_id: remote_utility_id,
    request_type: data.request_type,
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getComplaintSubCategory = async (
  remote_utility_id: string,
  code?: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "complaint_sub_type");
      if (code) {
        query.push("code_list", code);
      }
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const EditComplaintRequest = async (
  data: updateComplaintCategory
): Promise<ComplaintCategory> => {
  const payload = {
    name: data.name,
    remote_utility_id: data.remote_utility_id,
    code: data.code,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createComplaintSubCategory = async (
  remote_utility_id: string,
  data: CreateServiceRequest
): Promise<ServiceCategory> => {
  const payload = {
    utility_service: data.utility_service,
    name: data.name,
    code: data.code,
    remote_utility_id: remote_utility_id,
    request_type: data.request_type,
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/support_requests_json/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllServiceConfig = async (
  remote_utility_id: string,
  page: any,
  limit: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "Service");
      query.push("show_inactive", "true");
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.results ?? [];
};

export const searchAllServiceConfig = async (
  remote_utility_id: string,
  page: any,
  limit: any,
  search_data?: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "Service");
      query.push("show_inactive", "true");
      query.push("page", page);
      query.push("limit", limit);
      if (search_data) {
        query.push("search_data", search_data);
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.results ?? [];
};

export const getServiceCategoryByUtility = async (
  remote_utility_id: string,
  utility_service?: string[]
): Promise<any[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "service_type");
      query.push("config_level", "service_type");
      query.push("show_inactive", "true");
      if (utility_service) {
        for (const utility of utility_service) {
          query.push("utility_service", utility);
        }
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getServiceSubCategoryByCategory = async (
  remote_utility_id: string,
  code_list?: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("config_level", "service_sub_type");
      query.push("remote_utility_id", remote_utility_id);
      if (code_list) {
        query.push("code_list", code_list);
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllPriority = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/priority_level",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllFieldTypes = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "choices/field_type",
    () => {}
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllNotificationTemplate = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/notification_template",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllAvabilitySettings = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/availability_setting",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const createServiceConfig = async (
  data: CreateServiceConfiguartion
): Promise<CreateServiceConfiguartion> => {
  const payload = {
    remote_utility_id: data.remote_utility_id,
    name: data.name,
    configuration_code: data.configuration_code,
    long_description: data.long_description,
    product_code: data.product_code,
    support_request_type: data.support_request_type,
    support_request_subtype: data.support_request_subtype,
    request_type: data.request_type,
    extra_data: data.extra_data,
  };

  try {
    const response = await cxApiClient.post(
      "/utility-request-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllComplaintConfig = async (
  remote_utility_id: string,
  page: any,
  limit: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "Complaint");
      query.push("show_inactive", "true");
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.results ?? [];
};

export const searchAllComplaintConfig = async (
  remote_utility_id: string,
  page: any,
  limit: any,
  search_data?: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "Complaint");
      query.push("show_inactive", "true");
      query.push("page", page);
      query.push("limit", limit);
      if (search_data) {
        query.push("search_data", search_data);
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.results ?? [];
};

export const getComplaintCategoryByUtility = async (
  remote_utility_id: string,
  utility_service?: string[]
): Promise<any[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("request_type", "complaint_type");
      query.push("config_level", "complaint_type");
      query.push("show_inactive", "true");
      if (utility_service) {
        for (const utility of utility_service) {
          query.push("utility_service", utility);
        }
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getComplaintSubCategoryByCategory = async (
  remote_utility_id: string,
  code_list?: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/support_requests_json",
    (query) => {
      query.push("config_level", "complaint_sub_type");
      query.push("remote_utility_id", remote_utility_id);
      if (code_list) {
        query.push("code_list", code_list);
      }
    }
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllSeverity = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/severity_level",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllFrequency = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/consumer_update_frequency",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const createComplaintConfig = async (
  data: CreateComplaintConfiguration
): Promise<CreateComplaintConfiguration> => {
  const payload = {
    remote_utility_id: data.remote_utility_id,
    name: data.name,
    configuration_code: data.configuration_code,
    long_description: data.long_description,
    product_code: data.product_code,
    support_request_type: data.support_request_type,
    support_request_subtype: data.support_request_subtype,
    request_type: data.request_type,
    extra_data: {
      severity_level: data.extra_data.severity_level,
      response_time: data.extra_data.response_time,
      resolution_time: data.extra_data.resolution_time,
      acknowlegment_time: data.extra_data.acknowlegment_time,
      field_force_required: data.extra_data.field_force_required,
      consumer_update_frequency: data.extra_data.consumer_update_frequency,
      requires_approval: data.extra_data.requires_approval,
    },
  };

  try {
    const response = await cxApiClient.post(
      "/utility-request-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateServiceConfiguration = async (
  data: updateServiceConfig,
  service_id: number
): Promise<updateServiceConfig> => {
  const payload = {
    remote_utility_id: data.remote_utility_id,
    long_description: data.long_description,
    product_code: data.product_code,
    support_request_type: data.support_request_type,
    support_request_subtype: data.support_request_subtype,
    request_type: data.request_type,
    extra_data: data.extra_data,
  };
  try {
    const response = await cxApiClient.patch(
      `/utility-request-configuration/${service_id}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const toggleServiceConfiguration = async (data: any): Promise<any> => {
  const { id, isActive } = data;

  const payload = {
    isActive,
  };

  try {
    const response = await cxApiClient.patch(
      `/utility-request-configuration/${id}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateComplaintConfig = async (
  data: UpdateComplaintConfiguration,
  complaint_id: number
): Promise<UpdateComplaintConfiguration> => {
  const payload = {
    remote_utility_id: data.remote_utility_id,
    long_description: data.long_description,
    product_code: data.product_code,
    support_request_type: data.support_request_type,
    support_request_subtype: data.support_request_subtype,
    request_type: data.request_type,
    extra_data: {
      severity_level: data.extra_data.severity_level,
      response_time: data.extra_data.response_time,
      resolution_time: data.extra_data.resolution_time,
      acknowlegment_time: data.extra_data.acknowlegment_time,
      consumer_update_frequency: data.extra_data.consumer_update_frequency,
      requires_approval: data.extra_data.requires_approval,
    },
  };

  try {
    const response = await cxApiClient.patch(
      `/utility-request-configuration/${complaint_id}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteServiceConfiguration = async (
  service_id: number
): Promise<CreateServiceConfiguartion> => {
  try {
    const response = await cxApiClient.delete(
      `/utility-request-configuration/${service_id}/`
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteComplaintConfiguration = async (
  complaint_id: number
): Promise<CreateServiceConfiguartion> => {
  try {
    const response = await cxApiClient.delete(
      `/utility-request-configuration/${complaint_id}/`
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getPaymentChannel = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/payment_channel",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const updatePaymentChannelStatus = async (
  data: updateStatusRequest
): Promise<ServiceCategory> => {
  const payload = {
    code: data.code,
    is_active: data.is_active,
    remote_utility_id: data.remote_utility_id,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/payment_channel/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createPaymentChannel = async (data: any): Promise<any> => {
  const payload = {
    code: data.code,
    name: data.name,
    processing_fee: Number(data.processing_fee),
    remark: data.remark,
    remote_utility_id: data.remote_utility_id,
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/payment_channel/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePaymentChannel = async (data: any): Promise<any> => {
  const payload = {
    name: data.name,
    code: data.code,
    processing_fee: Number(data.processing_fee),
    remark: data.remark,
    remote_utility_id: data.remote_utility_id,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/payment_channel/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getPaymentMode = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/payment_mode",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const updatePaymentModeStatus = async (
  data: updateStatusRequest
): Promise<ServiceCategory> => {
  const payload = {
    code: data.code,
    is_active: data.is_active,
    remote_utility_id: data.remote_utility_id,
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/payment_mode/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createPaymentMode = async (data: any): Promise<any> => {
  const payload = {
    name: data.name,
    code: data.code,
    remark: data.remark,
    remote_utility_id: data.remote_utility_id,
    custom_fields: data.custom_fields || [], // Use the custom_fields from data or empty array
  };

  try {
    const response = await onboardingApiClient.post(
      "config-map/payment_mode/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePaymentMode = async (data: any): Promise<any> => {
  const payload = {
    code: data.code,
    remark: data.remark,
    remote_utility_id: data.remote_utility_id,
    custom_fields: data.custom_fields || [], // Use the custom_fields from data or empty array
  };

  try {
    const response = await onboardingApiClient.patch(
      "config-map/payment_mode/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getDisconnectionReasons = async (
  remote_utility_id: string,
  page: number,
  limit: number
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("show_inactive", "true");
      query.push("request_type", "Disconnect Permanent");
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data ?? [];
};

export const createReason = async (data: any): Promise<any> => {
  const payload = {
    name: data.name,
    configuration_code: data.configuration_code,
    long_description: data.long_description,
    request_type: data.request_type,
    remote_utility_id: data.remote_utility_id,
    extra_data: data.extra_data ?? {},
  };

  try {
    const response = await cxApiClient.post(
      "utility-request-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateReason = async (data: any): Promise<any> => {
  const payload = {
    name: data.name,
    configuration_code: data.configuration_code,
    long_description: data.long_description,
    request_type: data.request_type,
    remote_utility_id: data.remote_utility_id,
    extra_data: data.extra_data ?? {},
  };

  try {
    const response = await cxApiClient.patch(
      `utility-request-configuration/${data.id}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteReason = async (data: any): Promise<any> => {
  try {
    const response = await cxApiClient.delete(
      `utility-request-configuration/${data.id}/`
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getReconnectionReasons = async (
  remote_utility_id: string,
  page: number,
  limit: number
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("show_inactive", "true");
      query.push("request_type", "Reconnection");
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data ?? [];
};

export const getTransferTypes = async (
  remote_utility_id: string,
  page: number,
  limit: number
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "utility-request-configuration",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("show_inactive", "true");
      query.push("request_type", "Transfer");
      query.push("page", page);
      query.push("limit", limit);
    }
  );

  const response = await cxApiClient.get(url);
  return response.data ?? [];
};

export const getAllPaymentFields = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "choices/payment_fields",
    () => {}
  );

  const response = await cxApiClient.get(url);
  return response.data.result ?? [];
};

export const deletePaymentMode = async (
  remote_utility_id: string,
  mode: any
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/payment_mode",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("code", mode.code);
      }
    );

    const response = await onboardingApiClient.delete(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const deletePaymentChannel = async (
  remote_utility_id: string,
  channel: any
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/payment_channel",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("code", channel.code);
      }
    );

    const response = await onboardingApiClient.delete(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createNscResponseTimeConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: {
      response_time: data.configuration_data.response_time,
      acknowledge_time: data.configuration_data.acknowledge_time,
      resolution_time: data.configuration_data.resolution_time,
    },
  };

  try {
    const response = await cxApiClient.post(
      "nsc-connection-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNscResponseTimeConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: {
      response_time: data.configuration_data.response_time,
      acknowledge_time: data.configuration_data.acknowledge_time,
      resolution_time: data.configuration_data.resolution_time,
    },
  };

  try {
    const response = await cxApiClient.patch(
      "nsc-connection-configuration/RESPONSE_TIME/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getNscResponseTimeConfiguration = async (
  remote_utility_id: string
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-connection-configuration",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
      }
    );

    const response = await cxApiClient.get(url);
    const result = response.data.result || response.data;

    // ✅ Automatically store in localStorage after successful fetch
    if (result) {
      localStorage.setItem("NSC-Configuration", JSON.stringify(result));
      console.log("✅ NSC configuration saved to localStorage");
    }

    return result;
  } catch (error) {
    console.error("❌ Failed to fetch NSC configuration:", error);
    throw error; // rethrow so hook/query can handle it
  }
};

export const getAllAddressComponent = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "choices/address_component",
    () => {}
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getDocumentType = async (params: {
  remote_utility_id: string;
  config_level: string;
}): Promise<any> => {
  const response = await onboardingApiClient.get(
    `config-map/document_json/?remote_utility_id=${params.remote_utility_id}&config_level=${params.config_level}`
  );
  return response.data.result;
};

export const updateNscDocumentTypeConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: data.configuration_data,
  };

  try {
    const response = await cxApiClient.patch(
      "nsc-connection-configuration/DOCUMENT_TYPE/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createNscDocumentTypeConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: data.configuration_data,
  };

  try {
    const response = await cxApiClient.post(
      "nsc-connection-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNscAddressComponentConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: data.configuration_data,
  };

  try {
    const response = await cxApiClient.patch(
      "nsc-connection-configuration/ADDRESS_DISPLAY/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createNscAddressComponentConfiguration = async (
  data: any
): Promise<any> => {
  const payload = {
    config_type: data.config_type,
    remote_utility_id: data.remote_utility_id,
    configuration_data: data.configuration_data,
  };

  try {
    const response = await cxApiClient.post(
      "nsc-connection-configuration/",
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getCategoryConfigMap = async (
  remote_utility_id: string
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("config_level", "category");
      }
    );

    const response = await onboardingApiClient.get(url);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const createFeeConfiguration = async (payload: any): Promise<any> => {
  try {
    const response = await cxApiClient.post(
      `nsc-category-configuration/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getCategoryWiseFees = async (
  remote_utility_id: string,
  category: string
): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-category-configuration",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("category", category);
      }
    );

    const response = await cxApiClient.get(url);
    return response.data.result;
  } catch (error) {
    throw error;
  }
};
export const getAllFees = async (remote_utility_id: string,searchQuery: string,page: number,limit: number): Promise<any> => {
  try {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-category-configuration",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("search_data", searchQuery);
        query.push("page", page);
        query.push("limit", limit);
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteFeeConfiguration = async (payload: any): Promise<any> => {
  try {
    const response = await cxApiClient.delete(
      `nsc-category-configuration/${encodeURIComponent(
        payload.remote_utility_id
      )}/${encodeURIComponent(payload.category)}/${encodeURIComponent(
        payload.fee_code
      )}/`
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const api = {
  nsc: {
    create: createNscResponseTimeConfiguration,
    get: getNscResponseTimeConfiguration,
    update: updateNscResponseTimeConfiguration,
    getAllAddressComponent: getAllAddressComponent,
    getDocumentType: getDocumentType,
    updateDocumentTypeConfiguration: updateNscDocumentTypeConfiguration,
    createDocumentTypeConfiguration: createNscDocumentTypeConfiguration,
    updateAddressComponentConfiguration: updateNscAddressComponentConfiguration,
    createAddressComponentConfiguration: createNscAddressComponentConfiguration,
  },
  service: {
    category: {
      create: createServiceCategory,
      get: getServiceCategory,
      search: searchServiceCategory,
      delete: deleteCategory,
      getByUtility: getServiceCategoryByUtility,
      updateStatus: updateRequestStatus,
    },
    subCategory: {
      create: createServiceSubCategory,
      get: getServiceSubCategory,
      getByCategory: getServiceSubCategoryByCategory,
      update: updateServiceSubCategory,
      delete: deleteSubCategory,
    },
    config: {
      create: createServiceConfig,
      getAll: getAllServiceConfig,
      search: searchAllServiceConfig,
      update: updateServiceConfiguration,
      delete: deleteServiceConfiguration,
    },
  },
  complaint: {
    category: {
      create: createComplaintCategory,
      getAll: getAllComplaintCategory,
      search: searchAllComplaintCategory,
      delete: deleteCategory,
      getByUtility: getComplaintCategoryByUtility,
    },
    subCategory: {
      create: createComplaintSubCategory,
      getByCategory: getComplaintSubCategoryByCategory,
      delete: deleteSubCategory,
    },
    config: {
      create: createComplaintConfig,
      getAll: getAllComplaintConfig,
      search: searchAllComplaintConfig,
      update: updateComplaintConfig,
      delete: deleteComplaintConfiguration,
    },
  },
  payment: {
    channel: {
      get: {
        getChannel: getPaymentChannel,
      },
      create: {
        createChannel: createPaymentChannel,
      },
      update: {
        updateChannelStatus: updatePaymentChannelStatus,
        updateChannel: updatePaymentChannel,
      },
      delete: {
        deleteChannel: deletePaymentChannel,
      },
    },
    mode: {
      get: {
        getMode: getPaymentMode,
        updateModeStatus: updatePaymentModeStatus,
        getAllFieldTypes: getAllFieldTypes,
        getAllPaymentFields: getAllPaymentFields,
      },
      create: {
        createMode: createPaymentMode,
      },
      update: {
        updateMode: updatePaymentMode,
      },
      delete: {
        deleteMode: deletePaymentMode,
      },
    },
  },
  operationalSLA: {
    disconnectionReasons: {
      get: getDisconnectionReasons,
      create: createReason,
      update: updateReason,
      delete: deleteReason,
    },
    reconnectionReasons: {
      get: getReconnectionReasons,
    },
    transferTypes: {
      get: getTransferTypes,
    },
  },
  utility: {
    getServices: getUtilityServices,
  },
  settings: {
    getPriority: getAllPriority,
    getSeverity: getAllSeverity,
    getFrequency: getAllFrequency,
    getAvailability: getAllAvabilitySettings,
    getNotificationTemplates: getAllNotificationTemplate,
    getCategoryConfigMap: getCategoryConfigMap,
    createFeeConfiguration: createFeeConfiguration,
    getCategoryWiseFees: getCategoryWiseFees,
    deleteFeeConfiguration: deleteFeeConfiguration,
    getAllFees: getAllFees,
  },
};
